import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const QuestionRow = ({ question, isExample, ASSET_BASE_URL, answer, handleInputChange, showHint, isReviewMode, isCorrect, isHydrated }) => {
  const name = question.question_text;
  const imageUrl = (question.options && question.options[0] && question.options[0].image_url)
    ? question.options[0].image_url
    : '';

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;

  return (
    <div className={`flex items-center justify-between p-3 rounded-2xl border-2 transition-all ${isExample ? 'bg-blue-50/50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800' : 'bg-white border-slate-100 dark:bg-slate-800/50 dark:border-slate-700 hover:border-blue-200 shadow-sm'}`}>
      <div className="flex items-center gap-4">
        {imageUrl && <img src={`${ASSET_BASE_URL}${imageUrl}`} alt={name} className="w-24 h-24 object-contain bg-white dark:bg-gray-700 rounded-xl p-1 border border-slate-200 dark:border-slate-600 shadow-sm" />}
        <span className="font-display font-bold text-lg text-slate-700 dark:text-slate-200">{name.replace(/\(Example\)/gi, '').trim()}</span>
      </div>
      <div className="flex items-center gap-3">
        {showIncorrectAnswerInReview ? (
          <div className="exercise-input-text text-center bg-red-50 px-3 py-1 rounded-lg border border-red-100">
            {question.user_answer?.answer
              ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold">{question.user_answer.answer}</span>
              : <span className="text-gray-400 mr-2 font-bold">__</span>
            }
            <span className="text-green-600 font-bold">{question.accepted_answers[0]}</span>
          </div>
        ) : (
          <input
            type="text"
            maxLength="1"
            value={isExample ? (question.accepted_answers && question.accepted_answers[0]) || '' : (showHint || (isReviewMode && isAnswerCorrect)) ? question.accepted_answers[0] : answer || ''}
            onChange={isExample ? undefined : (e) => handleInputChange(question.id, e.target.value)}
            readOnly={isExample || showHint || !isHydrated}
            className={`exercise-input-field exercise-input-text w-14 h-14 text-2xl text-center uppercase !rounded-xl ${isExample ? 'exercise-example-text' : ''} ${showHint && !isExample ? 'hint-input' : ''}`}
            disabled={isReviewMode || !isHydrated}
          />
        )}
        {isReviewMode && (isAnswerCorrect
          ? <span className="correct-checkmark">✓</span>
          : <span className="incorrect-checkmark">✗</span>
        )}
        {isReviewMode && !isExample && <QuestionLearnButton question={question} />}
      </div>
    </div>
  );
};

const ListenPart3 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);


  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    shared_options,
  } = exerciseData.content_data;

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestion = sortedQuestions.find(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value.toUpperCase() }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10">
      <div className="relative flex flex-col justify-center items-center h-full bg-transparent dark:from-slate-800/50 dark:to-card-dark rounded-2xl p-2 md:p-6 overflow-y-auto custom-scrollbar">
        <div className="max-w-3xl w-full bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-3 md:p-8 shadow-sm">
          <div className="flex flex-col gap-2">
            {exampleQuestion &&
              <QuestionRow
                question={exampleQuestion}
                isExample={true}
                ASSET_BASE_URL={ASSET_BASE_URL}
                answer={answers[exampleQuestion.id]}
                handleInputChange={handleInputChange}
                isReviewMode={isReviewMode}
                isCorrect={exampleQuestion.is_correct}
                isHydrated={isHydrated}
              />}
            {mainQuestions.map(q =>
              <QuestionRow
                key={q.id}
                question={q}
                isExample={false}
                ASSET_BASE_URL={ASSET_BASE_URL}
                answer={answers[q.id]}
                handleInputChange={handleInputChange}
                showHint={showHint}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
              />)}
          </div>
        </div>

        {shared_options && shared_options.length > 0 && (
          <div className="md:hidden mt-6 bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-6 shadow-sm">
            <div className="grid grid-cols-2 gap-4">
              {shared_options.map(option => (
                <div key={option.id} className="flex flex-col items-center gap-3 p-4 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border-2 border-gray-200 dark:border-slate-700 hover:shadow-md transition-all">
                  <img src={`${ASSET_BASE_URL}${option.image_url}`} alt={`Option ${option.id}`} className="w-full h-auto object-contain rounded-xl" />
                  <span className="font-display font-bold text-xl text-amber-600 bg-amber-50 dark:bg-amber-900/30 w-9 h-9 rounded-full flex items-center justify-center border border-gray-200">{option.id}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListenPart3;